import React, { useEffect, useState } from 'react';
import { GIButton, Modal } from 'app-components';
import { FormattedMessage } from 'react-intl/dist';
import styled from 'styled-components';
import useApiHandling from '../../hooks/useApiHandling';
import DatePicker from '../../../../lib/inputs/react-datepicker';
import CommonTextArea from '../../common/input/CommonTextArea';
import {
    INPUT_MAX_LENGTH,
    ValidationErrorMessage,
    ICsrOverideSpecialEnrollmentModalProps,
    validateStartBeforeEnd,
} from '../../utils';
import { MM_SLASH_DD_SLASH_YYYY } from '../../../../utils/dates/dateFormats.utils';
import { formatDate } from '../../../../utils/dates/date.utils';

const StyledDiv = styled.div`
    @media (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
    }
`;
const StyledField = styled.div`
    .date-picker {
        margin-top: 4px !important;
        padding: 8px;
        background-color: ${({ theme }) => theme.dangerLightest};
    }
    @media (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
    }

    .gi-input {
        text-align: left;
        margin: 0;
    }
`;
const StyledPlanShoppingWindow = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 20px;
    text-align: left;
    height: 60px;
    align-items: center;
    margin-top: 20px;
    .w-360 {
        width: 360px;
    }
    @media (max-width: 500px) {
        flex-direction: column;
        gap: 5px;
    }
`;

const StyledControls = styled.div`
    display: flex;
    justify-content: space-between;
    .gap-10 {
        display: flex;
        gap: 10px;
    }
    @media (max-width: 768px) {
        margin-top: 0;
        flex-direction: column;
        gap: 5px;
    }
`;

const ContentAlert = styled.div`
    padding: 8px 16px;
    background: ${({ theme }) => theme.primaryLightest};
    border-radius: 2px;
    color: ${({ theme }) => theme['border-color-alert-info']};
    margin-bottom: 20px;
`;

const StyledBodyHeader = styled.h4`
    background-color: ${({ theme }) => theme.gray9};
    padding: 10px;
`;

const StyledErrorMessage = styled.div`
    margin-bottom: 40px;
    span {
        margin-top: 5px !important;
        @media (max-width: 500px) {
            margin-top: 25px !important;
        }
    }
`;

const InputContainer = styled.div`
    margin: 20px 0;
`;
const Label = styled.label`
    font-size: 16px;
    color: ${({ theme }) => theme['border-btn-outline-hover--blue']};
    margin-bottom: 5px;
    display: block;

    span {
        color: ${({ theme }) => theme.dangerMain};
        margin-left: 5px;
    }
`;

const OverrideSpecialEntrollmentDetailsModal = ({
    openModal,
    closeModal,
    data,
    applicantUpdatedSepEvents,
    application,
}: ICsrOverideSpecialEnrollmentModalProps) => {
    const [startDate, setStartDate] = useState<Date | null>(new Date(data.sepStartDate));
    const [endDate, setEndDate] = useState<Date | null>(new Date(data.sepEndDate));
    const [overrideComment, setOverrideComment] = useState('');
    const [dateError, setDateError] = useState({
        isUpdated: false,
        startBeforeEnd: false,
        future: false,
        invalidStartDate: false,
        invalidEndDate: false,
    });
    const { saveSepOverride } = useApiHandling();

    useEffect(() => {
        validateStartBeforeEnd(startDate, endDate, setDateError, data, applicantUpdatedSepEvents);
    }, [startDate, endDate]);

    function onBack() {
        openModal('csrOverrideEnrollmentModal', { data });
        closeModal('overrideSpecialEntrollmentDetailsModal');
    }

    function onClose() {
        closeModal('overrideSpecialEntrollmentDetailsModal');
    }

    function onSave() {
        saveSepOverride(
            {
                ...data,
                sepStartDate: formatDate(startDate as Date, MM_SLASH_DD_SLASH_YYYY),
                sepEndDate: formatDate(endDate as Date, MM_SLASH_DD_SLASH_YYYY),
                overrideCommentText: overrideComment,
            },
            applicantUpdatedSepEvents,
            application.coverageYear,
        );
    }

    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date: Date | null) => {
        setEndDate(date);
    };

    return (
        <Modal id="override_sep_qep_denial_modal">
            <Modal.Header
                id="override_sep_qep_denial_modal_title"
                titleId="cap.application.modal.specialEnrollment.header"
                onHide={onClose}
            />
            <Modal.Body>
                <StyledDiv>
                    <ContentAlert>
                        <FormattedMessage id="cap.application.modal.specialEnrollment.header2" />
                    </ContentAlert>

                    <StyledBodyHeader>
                        <FormattedMessage id="cap.application.modal.sepEnrollmentPeriods" />
                    </StyledBodyHeader>
                    <StyledPlanShoppingWindow>
                        <div className="w-360">
                            <StyledField>
                                <DatePicker
                                    labelText="common.start_date"
                                    wrapperClasses={['date-picker']}
                                    isRequired
                                    id="dob"
                                    type="text"
                                    pattern="MM/DD/YYYY"
                                    value={startDate}
                                    withMask={true}
                                    onDateChange={value => handleStartDateChange(value || null)}
                                />
                            </StyledField>
                        </div>
                        <div className="w-360">
                            <StyledField>
                                <DatePicker
                                    labelText="common.end_date"
                                    wrapperClasses={['date-picker']}
                                    isRequired
                                    id="dob"
                                    type="text"
                                    pattern="MM/DD/YYYY"
                                    value={endDate}
                                    withMask={true}
                                    onDateChange={value => handleEndDateChange(value || null)}
                                />
                            </StyledField>
                        </div>
                    </StyledPlanShoppingWindow>
                    <StyledErrorMessage>
                        <ValidationErrorMessage
                            isVisible={!startDate}
                            messageText={
                                'cap.application.modal.overrideSpecialEnrollmentDetailsModal.error.startDateRequired'
                            }
                        />
                        <ValidationErrorMessage
                            isVisible={!startDate && dateError.invalidStartDate}
                            messageText={
                                'cap.application.modal.overrideSpecialEnrollmentDetailsModal.error.invalidStartDate'
                            }
                        />
                        <ValidationErrorMessage
                            isVisible={!endDate}
                            messageText={
                                'cap.application.modal.overrideSpecialEnrollmentDetailsModal.error.endDateRequired'
                            }
                        />
                        <ValidationErrorMessage
                            isVisible={endDate && dateError.invalidEndDate}
                            messageText={
                                'cap.application.modal.overrideSpecialEnrollmentDetailsModal.error.invalidEndDate'
                            }
                        />
                        <ValidationErrorMessage
                            isVisible={endDate && dateError.future}
                            messageText={
                                'cap.application.modal.overrideSpecialEnrollmentDetailsModal.error.pastEndDate'
                            }
                        />
                        <ValidationErrorMessage
                            isVisible={
                                !dateError.invalidEndDate && !dateError.invalidStartDate && dateError.startBeforeEnd
                            }
                            messageText={
                                'cap.application.modal.overrideSpecialEnrollmentDetailsModal.error.endDateBeforeStart'
                            }
                        />
                    </StyledErrorMessage>

                    <StyledBodyHeader>
                        <FormattedMessage id="cap.applications.modal.overrideReason" />
                    </StyledBodyHeader>
                    <InputContainer>
                        <Label data-testid="override_sep_qep_denial_modal_input_label">
                            <FormattedMessage id="cap.application.modal.overrideSpecialEnrollmentDetailsModal.specifyReason" />
                            : <span>*</span>
                        </Label>
                        <CommonTextArea
                            id="override_sep_qep_denial_modal_textarea"
                            value={overrideComment}
                            setValue={(value: string) => value.length <= INPUT_MAX_LENGTH && setOverrideComment(value)}
                            placeholder={'cap.applications.modal.explainPlaceholder'}
                        />
                    </InputContainer>
                    <div>
                        {INPUT_MAX_LENGTH - overrideComment.trim().length} {''}
                        <FormattedMessage id="cap.applications.modal.charactersLeft" />
                    </div>
                </StyledDiv>
            </Modal.Body>
            <Modal.Controls>
                <StyledControls>
                    <GIButton id="button" onClickHandler={onBack} variant="secondary" labelId="button.back" />
                    <div className="gap-10">
                        <GIButton
                            id="button"
                            onClickHandler={onSave}
                            variant="primary"
                            labelId="button.save"
                            isDisabled={Object.values(dateError).some(Boolean) || !overrideComment.length}
                        />
                        <GIButton id="button" onClickHandler={onClose} variant="secondary" labelId="button.cancel" />
                    </div>
                </StyledControls>
            </Modal.Controls>
        </Modal>
    );
};

export default OverrideSpecialEntrollmentDetailsModal;
